import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import StarRateIcon from "@mui/icons-material/StarRate";
import { useNavigate } from "react-router-dom";
import {
  useGetShopByIdQuery,
  useGetShopItemsQuery,
} from "../../features/shop/shopApiSlice";
import { useGetServiceDetailQuery } from "../../features/service/serviceApiSlice";
import { useGetCompanyByIdQuery } from "../../features/user/userApiSlice";
import { useGetShopVerificationQuery } from "../../features/shopVerification/shopVerificationApiSlice";
import { useGetShopLevelRankingQuery } from "../../features/levelRanking/levelRankingApiSlice";
import {
  getShopStarCount,
  getShopVerificationStatus,
  calculateShopStarRank,
} from "../../utils/shopStarRank";
import "./shopStarRank.css";

const ShopStarRankLink = ({
  shop,
  shopId,
  enableServiceFallback = false,
  enableCompanyFallback = false,
  compact = false,
  businessType,
}) => {
  const navigate = useNavigate();
  const {
    data: shopResponse,
    isError: isShopError,
    isSuccess: isShopSuccess,
    isLoading: isShopLoading,
  } = useGetShopByIdQuery(shopId, {
    skip: Boolean(shop) || !shopId,
  });
  const shopRecord = shop || shopResponse?.data?.Shop;
  const {
    data: serviceResponse,
    isError: isServiceError,
    isSuccess: isServiceSuccess,
  } = useGetServiceDetailQuery(shopId, {
    skip:
      !enableServiceFallback ||
      Boolean(shopRecord) ||
      !shopId ||
      isShopLoading,
  });
  const serviceRecord = serviceResponse?.data?.Service;
  const serviceLookupFinished =
    !enableServiceFallback || isServiceError || isServiceSuccess;
  const {
    data: companyResponse,
    isError: isCompanyError,
    isSuccess: isCompanySuccess,
  } = useGetCompanyByIdQuery(shopId, {
    skip:
      !enableCompanyFallback ||
      Boolean(shopRecord) ||
      Boolean(serviceRecord) ||
      !shopId ||
      isShopLoading ||
      !serviceLookupFinished,
  });
  const companyRecord = companyResponse?.data?.Company;
  const shopData = shopRecord || serviceRecord || companyRecord;
  const targetId = shopData?._id || shopId;

  const shopOwnerUserId = shopData?.userid?._id || shopData?.userid;
  const { data: verificationRes } = useGetShopVerificationQuery(targetId, {
    skip: !targetId,
  });
  const { data: rankingRes } = useGetShopLevelRankingQuery(
    { shopId: targetId, userId: shopOwnerUserId },
    { skip: !targetId },
  );
  const { data: itemsResponse } = useGetShopItemsQuery(
    { shopid: targetId, page: 1, limit: 10 },
    { skip: !targetId },
  );

  const serverVerificationStatus = verificationRes?.data?.status;
  const isServerVerified = verificationRes?.data?.isVerified;

  const verificationStatus = isServerVerified
    ? "Verified"
    : serverVerificationStatus === "Pending"
      ? "Pending"
      : serverVerificationStatus === "Approved"
        ? "Verified"
        : serverVerificationStatus === "Rejected"
          ? "Rejected"
          : getShopVerificationStatus(shopData);

  const itemCount =
    Array.isArray(itemsResponse?.regular) || Array.isArray(itemsResponse?.specialoffer)
      ? ["regular", "specialoffer", "salespromotion"].reduce(
          (total, key) =>
            total + (Array.isArray(itemsResponse?.[key]) ? itemsResponse[key].length : 0),
          0,
        )
      : typeof itemsResponse?.total === "number"
      ? itemsResponse.total
      : typeof shopData?.itemCount === "number"
      ? shopData.itemCount
      : undefined;

  const evaluation = calculateShopStarRank({
    shop: shopData,
    verificationStatus,
    itemsCount: itemCount,
    connections: Number(shopData?.connections || shopData?.connectionsCount || rankingRes?.metrics?.totalConnect || 0),
    visitors: Number(shopData?.views || shopData?.visitorCount || 0),
    averageRating: Number(shopData?.ratings || shopData?.rating || rankingRes?.metrics?.averageRating || 0),
    completedGrowthLevels:
      rankingRes?.progression?.completedLevelsCount ??
      shopData?.completedLevel ??
      undefined,
  });

  const starCount =
    (typeof rankingRes?.progression?.starCount === "number"
      ? rankingRes.progression.starCount
      : null) ??
    (typeof rankingRes?.metrics?.starCount === "number"
      ? rankingRes.metrics.starCount
      : null) ??
    (typeof shopData?.starCount === "number"
      ? shopData.starCount
      : null) ??
    evaluation.totalStars;

  const currentRank =
    (typeof rankingRes?.progression?.currentRank === "number" && rankingRes.progression.currentRank > 0
      ? rankingRes.progression.currentRank
      : null) ??
    (typeof shopData?.starRank === "number" && shopData.starRank > 0
      ? shopData.starRank
      : null) ??
    evaluation.starRank;

  const rankingUrl = `/shop-star-progress/${targetId}${
    businessType ? `?businessType=${encodeURIComponent(businessType)}` : ""
  }`;

  const bothLookupsFinished =
    (isShopError || isShopSuccess) &&
    serviceLookupFinished &&
    (!enableCompanyFallback || isCompanyError || isCompanySuccess);
  if (!targetId || (!shopData && bothLookupsFinished)) return null;

  return (
    <>
      <div className="shop-star-profile-badge">
        <span className="shop-star-profile-chip">
          <StarRateIcon fontSize="inherit" />
          {starCount} / 10 STARS
        </span>
        <span>STAR RANK</span>
      </div>
      <button
        type="button"
        className={`shop-star-profile-progress${compact ? " compact" : ""}`}
        onClick={() => navigate(rankingUrl)}
      >
        <span className="shop-star-profile-progress-top">
          <span><VerifiedUserIcon fontSize="inherit" /> SELECTEZI STAR RANK</span>
          <strong>Rank {currentRank} of 10</strong>
        </span>
        <span className={`shop-star-profile-progress-status${compact ? " compact" : ""}`}>
          {!compact ? <span>Shop verification status</span> : null}
          <strong className={`shop-star-status ${verificationStatus.toLowerCase().replace(/\s/g, "-")}`}>
            {verificationStatus}
          </strong>
        </span>
        <span className={`shop-star-profile-progress-button${compact ? " compact" : ""}`}>
          View Star Progress <span aria-hidden="true">→</span>
        </span>
      </button>
    </>
  );
};

export default ShopStarRankLink;
